import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import "bootstrap/dist/css/bootstrap.min.css";
import Content from "../../Components/Core/Content";
import FormInput from "../../Components/Core/FormInput";
import {
  requestLoginCode,
  login,
  loginWithPassword,
  isLoggedIn,
} from "../../Services/AuthService";
import { useLocation } from "react-router-dom";
import Notification from "../../Components/Core/Notification";

// Styled Components with Premium Medical Design
const LoginContainer = styled("div")(({ theme }) => ({
  background: "linear-gradient(135deg, #e5eff3ff 0%, #ffffff 100%)",
  minHeight: "100vh",
  padding: "40px 20px",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "radial-gradient(ellipse at center, rgba(124, 200, 144, 0.08) 0%, transparent 70%)",
    pointerEvents: "none",
  },

  "@media(max-width: 768px)": {
    padding: "30px 0px",
  },
}));

const LoginCard = styled("div")(({ theme }) => ({
  background:
    "linear-gradient(145deg, #ffffff 0%, rgba(248, 255, 254, 0.95) 100%)",
  borderRadius: "32px",
  boxShadow:
    "0 32px 80px rgba(5, 50, 81, 0.12), 0 16px 40px rgba(5, 50, 81, 0.08)",
  border: "1px solid rgba(124, 200, 144, 0.15)",
  padding: "48px 40px",
  position: "relative",
  overflow: "hidden",
  maxWidth: "600px",
  margin: "0 auto",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "8px",
    background: "linear-gradient(90deg, #053251 0%, #7cc890 100%)",
    borderRadius: "32px 32px 0 0",
  },
  "@media (max-width: 768px)": {
    padding: "32px 24px",
    borderRadius: "24px",
    margin: "0 16px",
  },
}));

const PageTitle = styled("h2")(({ theme }) => ({
  fontSize: "32px",
  fontWeight: "700",
  background: "linear-gradient(135deg, #053251 0%, #2a5a72 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  letterSpacing: "-1px",
  textAlign: "center",
  marginBottom: "40px",
  position: "relative",
  paddingBottom: "16px",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "80px",
    height: "3px",
    background: "linear-gradient(90deg, #7cc890 0%, #053251 100%)",
    borderRadius: "2px",
  },
  "@media (max-width: 768px)": {
    fontSize: "24px",
    marginBottom: "32px",
  },
}));

const FormSection = styled("div")(({ theme }) => ({
  marginBottom: "32px",
  position: "relative",
}));

const StyledButton = styled(Button)(
  ({ theme, variant: buttonVariant, disabled: isDisabled }) => ({
    borderRadius: "16px",
    padding: "14px 32px",
    fontSize: "16px",
    fontWeight: "600",
    textTransform: "none",
    letterSpacing: "0.5px",
    minWidth: "160px",
    position: "relative",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "none",
    marginTop: "16px",
    marginLeft: "8px",

    ...(buttonVariant === "contained" &&
      !isDisabled && {
        background: "linear-gradient(135deg, #053251 0%, #2a5a72 100%)",
        color: "white",
        border: "1px solid rgba(5, 50, 81, 0.3)",
        "&:hover": {
          background: "linear-gradient(135deg, #2a5a72 0%, #1e4159 100%)",
          boxShadow: "0 12px 24px rgba(5, 50, 81, 0.25)",
          transform: "translateY(-2px)",
        },
        "&:active": {
          transform: "translateY(0)",
        },
      }),

    ...(isDisabled && {
      background:
        "linear-gradient(135deg, rgba(5, 50, 81, 0.3) 0%, rgba(5, 50, 81, 0.2) 100%)",
      color: "rgba(255, 255, 255, 0.7)",
      cursor: "not-allowed",
    }),

    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "16px",
      padding: "1px",
      background: isDisabled
        ? "transparent"
        : "linear-gradient(135deg, rgba(124, 200, 144, 0.4), transparent)",
      mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      maskComposite: "xor",
      WebkitMask:
        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      WebkitMaskComposite: "xor",
    },

    "@media (max-width: 768px)": {
      padding: "12px 24px",
      fontSize: "15px",
      width: "100%",
      marginLeft: "0",
      marginBottom: "12px",
    },
  })
);

const ButtonContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "16px",
  marginTop: "32px",

  "@media (max-width: 768px)": {
    flexDirection: "column",
    gap: "8px",
  },
}));

const TimerText = styled("span")(({ theme }) => ({
  color: "#ffffffff",
  fontSize: "14px",
  fontWeight: "500",
  opacity: 0.8,
}));

const LoadingSpinner = styled(CircularProgress)(({ theme }) => ({
  color: "white",

  "& .MuiCircularProgress-circle": {
    stroke: "white",
  },
}));

const ModeToggle = styled("div")({
  display: "flex",
  background: "rgba(5, 50, 81, 0.06)",
  borderRadius: "16px",
  padding: "4px",
  marginBottom: "32px",
  gap: "4px",
  fontFamily: '"MyPersianFont", sans-serif',
});

const ModeButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  flex: 1,
  border: "none",
  borderRadius: "12px",
  padding: "12px 16px",
  fontWeight: 600,
  fontSize: "15px",
  fontFamily: '"MyPersianFont", sans-serif',
  background: active
    ? "linear-gradient(135deg, #053251 0%, #2a5a72 100%)"
    : "transparent",
  color: active ? "white" : "#053251",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "& span": {
    fontFamily: '"MyPersianFont", sans-serif',
    fontWeight: 600,
  },
  "@media (max-width: 768px)": {
    fontSize: "13px",
    padding: "10px 8px",
  },
}));

const HintText = styled("p")({
  textAlign: "center",
  color: "#2a5a72",
  fontSize: "13px",
  lineHeight: 1.7,
  marginTop: "8px",
  opacity: 0.85,
  fontFamily: '"MyPersianFont", sans-serif',
});

export const Login = () => {
  const [loginMode, setLoginMode] = useState("otp");
  const [mobileNumber, setMobileNumber] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [password, setPassword] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  useEffect(() => {
    document.title = "ورود";
    if (isLoggedIn()) {
      navigate(from);
    }
    let interval;
    if (isCodeSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isCodeSent, timer, navigate, from]);

  const handleModeChange = (mode) => {
    setLoginMode(mode);
    setIsCodeSent(false);
    setConfirmationCode("");
    setPassword("");
    setTimer(0);
  };

  const finishLogin = (data) => {
    if (data.isProfileComplete) {
      setSnackbarMessage("با موفقیت وارد شدید!");
      setSnackbarSeverity("success");
      setTimeout(() => {
        navigate(from);
      }, 1500);
    } else {
      navigate("/complete-profile", {
        state: { from: from },
      });
    }
  };

  const handleRequestCode = async () => {
    setLoading(true);
    try {
      await requestLoginCode(mobileNumber);
      setIsCodeSent(true);
      setTimer(60);
    } catch (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setMobileNumber("");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      const data = await login(mobileNumber, confirmationCode);
      finishLogin(data);
    } catch (error) {
      console.log(error.message);
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
      setOpenSnackbar(true);
    }
  };

  const handlePasswordLogin = async () => {
    setLoading(true);
    try {
      const data = await loginWithPassword(mobileNumber, password);
      finishLogin(data);
    } catch (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      <LoginContainer>
        <Content>
          <LoginCard>
            <PageTitle className="mb-4 row mt-5">
              <span>ورود به حساب کاربری</span>
            </PageTitle>

            <div className="row justify-content-center">
              <div className="col-md-8 col-12">
                <ModeToggle>
                  <ModeButton
                    type="button"
                    active={loginMode === "otp"}
                    onClick={() => handleModeChange("otp")}
                  >
                    <span>ورود با کد پیامک</span>
                  </ModeButton>
                  <ModeButton
                    type="button"
                    active={loginMode === "password"}
                    onClick={() => handleModeChange("password")}
                  >
                    <span>ورود با رمز عبور</span>
                  </ModeButton>
                </ModeToggle>
              </div>
            </div>

            <FormSection>
              <div className="row justify-content-center">
                <div className="col-md-8 col-12">
                  <FormInput
                    name={"شماره موبایل"}
                    onChange={(e, value) => setMobileNumber(value)}
                    value={mobileNumber}
                    for={"mobileNumber"}
                    styleClasses={"PhoneInput py-2 px-3 fs-4"}
                    dir="ltr"
                    disabled={loginMode === "otp" && isCodeSent}
                    numericOnly={true}
                    autoComplete="tel"
                  />
                </div>
              </div>
            </FormSection>

            {loginMode === "otp" && isCodeSent && (
              <FormSection>
                <div className="row justify-content-center">
                  <div className="col-md-8 col-12">
                    <FormInput
                      name={"کد تایید"}
                      onChange={(e, value) => setConfirmationCode(value)}
                      value={confirmationCode}
                      for={"confirmationCode"}
                      styleClasses={"PhoneInput py-2 px-3 fs-4"}
                      dir="ltr"
                      numericOnly={true}
                    />
                  </div>
                </div>
              </FormSection>
            )}

            {loginMode === "password" && (
              <FormSection>
                <div className="row justify-content-center">
                  <div className="col-md-8 col-12">
                    <FormInput
                      name={"رمز عبور"}
                      type="password"
                      onChange={(e, value) => setPassword(value)}
                      value={password}
                      for={"password"}
                      styleClasses={"PhoneInput py-2 px-3 fs-4"}
                      dir="ltr"
                      autoComplete="current-password"
                    />
                    <HintText>
                      اگر هنوز رمز عبور تنظیم نکرده‌اید، ابتدا با کد پیامک وارد
                      شوید و از بخش پروفایل رمز تعیین کنید.
                    </HintText>
                  </div>
                </div>
              </FormSection>
            )}

            <ButtonContainer>
              {loginMode === "password" ? (
                <StyledButton
                  variant="contained"
                  color="primary"
                  onClick={handlePasswordLogin}
                  disabled={loading || mobileNumber.length < 10 || !password}
                >
                  {loading ? <LoadingSpinner size={24} /> : <span>ورود</span>}
                </StyledButton>
              ) : !isCodeSent ? (
                <StyledButton
                  variant="contained"
                  color="primary"
                  onClick={handleRequestCode}
                  disabled={loading || mobileNumber.length < 10}
                >
                  {loading ? (
                    <LoadingSpinner size={24} />
                  ) : (
                    <span>درخواست کد ورود</span>
                  )}
                </StyledButton>
              ) : (
                <>
                  <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={handleLogin}
                    disabled={loading || confirmationCode.length < 6}
                  >
                    {loading ? <LoadingSpinner size={24} /> : <span>ورود</span>}
                  </StyledButton>
                  <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={handleRequestCode}
                    disabled={timer > 0 || loading || mobileNumber.length < 10}
                  >
                    <TimerText>
                      {timer > 0
                        ? `درخواست مجدد کد ورود (${timer} ثانیه)`
                        : "درخواست مجدد کد ورود"}
                    </TimerText>
                  </StyledButton>
                </>
              )}
            </ButtonContainer>
          </LoginCard>
        </Content>
      </LoginContainer>

      <Notification
        openSnackbar={openSnackbar}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        setOpenSnackbar={setOpenSnackbar}
      />
    </>
  );
};
